import PropTypes from "prop-types";

import { NUMERICAL_GRADE_BANDS } from "@/constants/GradeAndLexileLevels";
import convertTimestampToDate from "@/utils/convertTimestampToDate";
import formatDuration from "@/utils/formatDuration";
import articleTypes from "@/view/constants/articleTypes";
import contentTypes from "@/view/constants/contentTypes";
import { isDecodableContent } from "@/view/utils/decodableContent";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import getBestMatchingMaturityBands, {
  getMaturityRange,
} from "@/view/utils/getBestMatchingMaturityBands/getBestMatchingMaturityBands";
import { detailsCss } from "./ContentDisplayDetails.style";
import ContentDisplayDetailsRow from "./ContentDisplayDetailsRow";
import StandardsRow from "./StandardsRow";

const propTypes = {
  __cssFor: PropTypes.shape({
    root: PropTypes.object,
  }),
  bannerMode: PropTypes.object.isRequired,
  contentData: PropTypes.object,
  contentMetadata: PropTypes.array,
  currentLevel: PropTypes.object,
  isStudent: PropTypes.bool,
  isTeacher: PropTypes.bool,
};

const unauthPropTypes = {
  contentData: PropTypes.object,
  currentLevel: PropTypes.object,
};

const { ARTICLE_POEM } = articleTypes;

const getRecommendedValue = (maturityRange) => {
  if (maturityRange) {
    const matchingMaturityBands = getBestMatchingMaturityBands(maturityRange);
    return getMaturityRange(matchingMaturityBands);
  }
};

const ContentDisplayDetails = ({
  __cssFor,
  bannerMode,
  contentData,
  contentMetadata,
  currentLevel = {},
  isStudent,
  isTeacher,
}) => {
  const { isStudentPreviewOn } = useStudentPreviewContext();
  const { isBannerModeActive } = bannerMode;
  const { language = "" } = currentLevel;

  const maturityRange = contentData?.maturity_range;

  const providerData = contentMetadata?.find(
    (datum) => datum.content_type === "PROVIDER",
  )?.data;
  const publicationData = contentData?.article_publications;
  const requiresPublicationDate =
    publicationData &&
    contentData?.content_type !== contentTypes.INTERACTIVE_VIDEO;
  const currentLanguagePublicationData =
    requiresPublicationDate &&
    publicationData?.find((publication) => publication.language === language);

  const recommendedForValue = getRecommendedValue(maturityRange);

  const textLevelValue = NUMERICAL_GRADE_BANDS[currentLevel?.grade_band];
  const durationTime = formatDuration(contentData?.length_in_seconds);
  const isDecodable = isDecodableContent(contentMetadata);

  const shouldShowByline = !!currentLevel?.byline;
  const shouldShowDurationTime = !!contentData?.length_in_seconds;
  const shouldShowRecommendedFor =
    !(isStudent || isBannerModeActive || !recommendedForValue) &&
    !isStudentPreviewOn;
  const shouldShowWordCount = contentData?.article_type !== ARTICLE_POEM;
  const shouldShowTextLevel =
    !isStudent && !isBannerModeActive && !isStudentPreviewOn && !isDecodable;
  const shouldShowPublishedAt = !!currentLanguagePublicationData;
  const shouldShowStandards =
    isTeacher && contentMetadata && !isBannerModeActive && !isStudentPreviewOn;

  const displayDetailsRows = {
    ARTICLE: [
      ...(shouldShowByline
        ? [
            [
              {
                hideLabel: true,
                readAloudIdentifier: "byline",
                shouldBeReadAloud: true,
                value: currentLevel?.byline,
              },
            ],
          ]
        : []),
      // Flatten the returned value. A flattened empty array will not take up additional space within the collection
      ...(shouldShowRecommendedFor
        ? [[{ label: "Recommended For", value: recommendedForValue }]]
        : []),
      [
        ...(shouldShowWordCount
          ? [{ label: "Words", value: currentLevel?.word_count }]
          : []),
        ...(shouldShowTextLevel
          ? [{ label: "Text Level", value: textLevelValue }]
          : []),
        ...(shouldShowPublishedAt
          ? [
              {
                label: "Published",
                readAloudIdentifier: "publishedAt",
                shouldBeReadAloud: true,
                value: convertTimestampToDate(
                  currentLanguagePublicationData.visible_after,
                ),
              },
            ]
          : []),
      ],
    ],
    INTERACTIVE_VIDEO: [
      [
        ...(providerData?.name
          ? [{ label: "By", value: providerData.name }]
          : []),
        ...(shouldShowDurationTime
          ? [{ label: "Duration", value: durationTime }]
          : []),
        ...(shouldShowRecommendedFor
          ? [{ label: "Recommended For", value: recommendedForValue }]
          : []),
      ],
    ],
    otherContentType: [],
  };

  const fieldRows = displayDetailsRows[contentData.content_type];
  return (
    <div css={[detailsCss, __cssFor?.root]}>
      {fieldRows.map((row, index) => {
        return <ContentDisplayDetailsRow row={row} key={index} />;
      })}
      {shouldShowStandards && (
        <StandardsRow contentMetadata={contentMetadata} />
      )}
    </div>
  );
};

const Unauth = ({ contentData = {}, currentLevel = {} }) => {
  const {
    language = "",
    byline,
    grade_band: gradeBand,
    word_count: wordCount,
  } = currentLevel;
  const {
    article_publications: publicationData,
    maturity_range: maturityRange,
  } = contentData;

  const currentLanguagePublicationData = publicationData?.find(
    (publication) => publication.language === language,
  );
  const recommendedForValue = getRecommendedValue(maturityRange);
  const textLevelValue = NUMERICAL_GRADE_BANDS[gradeBand];

  const shouldShowByline = !!byline;

  const fieldRows = [
    ...(shouldShowByline
      ? [
          [
            {
              hideLabel: true,
              value: byline,
            },
          ],
        ]
      : []),
    [{ label: "Recommended For", value: recommendedForValue }],
    [
      { label: "Words", value: wordCount },
      { label: "Text Level", value: textLevelValue },
      ...(currentLanguagePublicationData
        ? [
            {
              label: "Published",
              value: convertTimestampToDate(
                currentLanguagePublicationData.visible_after,
              ),
            },
          ]
        : []),
    ],
  ];

  return (
    <div css={detailsCss}>
      {fieldRows.map((row, index) => {
        return <ContentDisplayDetailsRow.Unauth row={row} key={index} />;
      })}
    </div>
  );
};

ContentDisplayDetails.propTypes = propTypes;
Unauth.propTypes = unauthPropTypes;

ContentDisplayDetails.Unauth = Unauth;

export default ContentDisplayDetails;
